import { useCallback, useEffect, useRef, useState } from "react";
import type { TonalCurvePoint, TonalCurveValues } from "../types";

interface TonalCurveEditorProps {
  value: TonalCurveValues;
  onChange: (value: TonalCurveValues) => void;
  onChangeEnd?: (value: TonalCurveValues) => void;
  disabled?: boolean;
}

const SVG_SIZE = 200;
const MARGIN = 10; // Margin around the curve area for endpoint visibility
const MIN_POINT_DISTANCE = 0.05;
const DRAG_STEP = 0.001;

export function TonalCurveEditor({
  value,
  onChange,
  onChangeEnd,
  disabled = false,
}: TonalCurveEditorProps) {
  const [draggedPointIndex, setDraggedPointIndex] = useState<number | null>(null);
  const draggedPointIndexRef = useRef<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Catmull-Rom spline interpolation for smooth curves
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Stupid warning
  const createSvgPath = useCallback((points: TonalCurvePoint[]) => {
    if (points.length < 2) {
      return "";
    }

    if (points.length === 2) {
      return `M ${points[0].x * SVG_SIZE} ${(1 - points[0].y) * SVG_SIZE} L ${points[1].x * SVG_SIZE} ${(1 - points[1].y) * SVG_SIZE}`;
    }

    // For Catmull-Rom spline, we need to create a path using multiple segments
    let path = `M ${points[0].x * SVG_SIZE} ${(1 - points[0].y) * SVG_SIZE}`;

    // Generate smooth curve through all points
    const segments = 20; // Number of segments between each pair of points

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = i === 0 ? points[0] : points[i - 1];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = i === points.length - 2 ? points[points.length - 1] : points[i + 2];

      for (let t = 0; t <= segments; t++) {
        const u = t / segments;
        const u2 = u * u;
        const u3 = u2 * u;

        // Catmull-Rom spline formula
        const x =
          0.5 *
          (2 * p1.x +
            (-p0.x + p2.x) * u +
            (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * u2 +
            (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * u3);

        const y =
          0.5 *
          (2 * p1.y +
            (-p0.y + p2.y) * u +
            (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * u2 +
            (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * u3);

        // Clamp interpolated values to 0-1 range before converting to SVG coordinates
        const clampedX = Math.max(0, Math.min(1, x));
        const clampedY = Math.max(0, Math.min(1, y));

        const svgX = clampedX * SVG_SIZE;
        const svgY = (1 - clampedY) * SVG_SIZE;

        if (t === 0 && i === 0) {
          // Already moved to first point
        } else {
          path += ` L ${svgX} ${svgY}`;
        }
      }
    }

    return path;
  }, []);

  const getSvgPoint = useCallback((clientX: number, clientY: number) => {
    if (!svgRef.current) {
      return { x: 0, y: 0 };
    }

    const rect = svgRef.current.getBoundingClientRect();
    // Account for margins - map to the curve area (0-200) within the padded SVG
    const relativeX = (clientX - rect.left) / rect.width;
    const relativeY = (clientY - rect.top) / rect.height;

    // Convert from SVG space to curve space (0-1), accounting for margins
    const x = Math.max(0, Math.min(1, (relativeX * (SVG_SIZE + 2 * MARGIN) - MARGIN) / SVG_SIZE));
    const y = Math.max(
      0,
      Math.min(1, 1 - (relativeY * (SVG_SIZE + 2 * MARGIN) - MARGIN) / SVG_SIZE)
    );

    return { x, y };
  }, []);

  const handleMouseDown = useCallback(
    (index: number, event: React.MouseEvent) => {
      if (disabled) {
        return;
      }

      draggedPointIndexRef.current = index;
      setDraggedPointIndex(index);
      event.preventDefault();
      event.stopPropagation();
    },
    [disabled]
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (draggedPointIndex === null || disabled) {
        return;
      }

      const svgPoint = getSvgPoint(event.clientX, event.clientY);
      const newPoints = [...value.points];

      // For locked endpoints, only allow Y movement
      if (draggedPointIndex === 0 || draggedPointIndex === value.points.length - 1) {
        newPoints[draggedPointIndex] = {
          x: newPoints[draggedPointIndex].x, // Keep X locked
          y: Math.max(0, Math.min(1, svgPoint.y)), // Clamp Y to 0-1
        };
      } else {
        // For other points, ensure minimum distance constraint
        const prevX = draggedPointIndex > 0 ? newPoints[draggedPointIndex - 1].x : 0;
        const nextX =
          draggedPointIndex < newPoints.length - 1 ? newPoints[draggedPointIndex + 1].x : 1;

        const constrainedX = Math.max(
          prevX + MIN_POINT_DISTANCE,
          Math.min(nextX - MIN_POINT_DISTANCE, svgPoint.x)
        );

        newPoints[draggedPointIndex] = {
          x: Math.max(0, Math.min(1, constrainedX)), // Clamp X to 0-1
          y: Math.max(0, Math.min(1, svgPoint.y)), // Clamp Y to 0-1
        };
      }

      onChange({ points: newPoints });
    },
    [draggedPointIndex, disabled, getSvgPoint, onChange, value.points]
  );

  const handleMouseUp = useCallback(() => {
    if (draggedPointIndexRef.current !== null && onChangeEnd) {
      onChangeEnd(value);
    }
    draggedPointIndexRef.current = null;
    setDraggedPointIndex(null);
  }, [onChangeEnd, value]);

  // Find which point is near a given position
  const findPointNearPosition = useCallback(
    (clientX: number, clientY: number): number | null => {
      if (!svgRef.current) return null;

      const rect = svgRef.current.getBoundingClientRect();
      const touchX = clientX - rect.left;
      const touchY = clientY - rect.top;

      const TOUCH_THRESHOLD = 30; // pixels

      for (let i = 0; i < value.points.length; i++) {
        const point = value.points[i];
        const pointScreenX = ((point.x * SVG_SIZE + MARGIN) / (SVG_SIZE + 2 * MARGIN)) * rect.width;
        const pointScreenY =
          (((1 - point.y) * SVG_SIZE + MARGIN) / (SVG_SIZE + 2 * MARGIN)) * rect.height;

        const distance = Math.sqrt(
          Math.pow(touchX - pointScreenX, 2) + Math.pow(touchY - pointScreenY, 2)
        );

        if (distance <= TOUCH_THRESHOLD) {
          return i;
        }
      }

      return null;
    },
    [value.points]
  );

  // Native touch event handlers on SVG
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const handleTouchStart = (event: TouchEvent) => {
      if (disabled || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const pointIndex = findPointNearPosition(touch.clientX, touch.clientY);

      if (pointIndex !== null) {
        event.preventDefault();
        draggedPointIndexRef.current = pointIndex;
        setDraggedPointIndex(pointIndex);
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      const currentIndex = draggedPointIndexRef.current;
      if (currentIndex === null || disabled || event.touches.length !== 1) return;

      event.preventDefault();
      const touch = event.touches[0];
      const svgPoint = getSvgPoint(touch.clientX, touch.clientY);
      const newPoints = [...value.points];

      if (currentIndex === 0 || currentIndex === value.points.length - 1) {
        newPoints[currentIndex] = {
          x: newPoints[currentIndex].x,
          y: Math.max(0, Math.min(1, svgPoint.y)),
        };
      } else {
        const prevX = currentIndex > 0 ? newPoints[currentIndex - 1].x : 0;
        const nextX = currentIndex < newPoints.length - 1 ? newPoints[currentIndex + 1].x : 1;

        const constrainedX = Math.max(
          prevX + MIN_POINT_DISTANCE,
          Math.min(nextX - MIN_POINT_DISTANCE, svgPoint.x)
        );

        newPoints[currentIndex] = {
          x: Math.max(0, Math.min(1, constrainedX)),
          y: Math.max(0, Math.min(1, svgPoint.y)),
        };
      }

      onChange({ points: newPoints });
    };

    const handleTouchEnd = () => {
      const currentIndex = draggedPointIndexRef.current;
      if (currentIndex !== null && onChangeEnd) {
        onChangeEnd(value);
      }
      draggedPointIndexRef.current = null;
      setDraggedPointIndex(null);
    };

    svg.addEventListener("touchstart", handleTouchStart, { passive: false });
    svg.addEventListener("touchmove", handleTouchMove, { passive: false });
    svg.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      svg.removeEventListener("touchstart", handleTouchStart);
      svg.removeEventListener("touchmove", handleTouchMove);
      svg.removeEventListener("touchend", handleTouchEnd);
    };
  }, [disabled, findPointNearPosition, getSvgPoint, onChange, onChangeEnd, value]);

  // Set up global mouse events for dragging
  useEffect(() => {
    const handleGlobalMouseMove = (event: MouseEvent) => handleMouseMove(event);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (draggedPointIndex !== null) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [draggedPointIndex, handleMouseMove, handleMouseUp]);

  const handleSvgClick = useCallback(
    (event: React.MouseEvent) => {
      if (disabled || value.points.length >= 8) {
        return; // Max 8 points including endpoints
      }

      const svgPoint = getSvgPoint(event.clientX, event.clientY);
      const newPoints = [...value.points];

      // Find where to insert the new point (maintain sorted order by X)
      let insertIndex = 1; // Start after the first locked point
      for (let i = 1; i < newPoints.length - 1; i++) {
        if (newPoints[i].x > svgPoint.x) {
          insertIndex = i;
          break;
        }
        insertIndex = i + 1;
      }

      // Ensure minimum distance from existing points
      const prevPoint = newPoints[insertIndex - 1];
      const nextPoint = newPoints[insertIndex];

      if (
        svgPoint.x - prevPoint.x < MIN_POINT_DISTANCE ||
        nextPoint.x - svgPoint.x < MIN_POINT_DISTANCE
      ) {
        return;
      }

      newPoints.splice(insertIndex, 0, {
        x: Math.round(svgPoint.x / DRAG_STEP) * DRAG_STEP,
        y: Math.round(svgPoint.y / DRAG_STEP) * DRAG_STEP,
      });

      const newValue = { points: newPoints };
      onChange(newValue);
      if (onChangeEnd) {
        onChangeEnd(newValue);
      }
    },
    [disabled, getSvgPoint, onChange, onChangeEnd, value.points]
  );

  const handlePointRightClick = useCallback(
    (index: number, event: React.MouseEvent) => {
      if (disabled) {
        return;
      }

      // Don't allow deletion of locked endpoints or if only 2 points remain
      if (index === 0 || index === value.points.length - 1 || value.points.length <= 2) {
        return;
      }

      const newPoints = value.points.filter((_, i) => i !== index);
      onChange({ points: newPoints });

      event.preventDefault(); // Prevent context menu
      event.stopPropagation();
    },
    [disabled, onChange, value.points]
  );

  return (
    <div className={`space-y-2 ${disabled ? "pointer-events-none opacity-50" : ""}`}>
      <div className="relative mx-auto aspect-square w-[60vw] flex-shrink-0 md:w-[16vw]">
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: Interactive SVG for curve editing */}
        <svg
          ref={svgRef}
          viewBox={`${-MARGIN} ${-MARGIN} ${SVG_SIZE + 2 * MARGIN} ${SVG_SIZE + 2 * MARGIN}`}
          className="cursor-crosshair rounded-lg border bg-gradient-to-br from-muted/20 to-muted/40"
          onClick={handleSvgClick}
          aria-label="Tonal curve editor - click to add points, drag to adjust"
          role="img"
        >
          {/* Grid lines */}
          <defs>
            <pattern
              id="curve-grid"
              width={SVG_SIZE / 4}
              height={SVG_SIZE / 4}
              patternUnits="userSpaceOnUse"
            >
              <path
                d={`M ${SVG_SIZE / 4} 0 L 0 0 0 ${SVG_SIZE / 4}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
                opacity="0.3"
              />
            </pattern>
            <linearGradient id="curve-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" className="[stop-color:hsl(var(--foreground))]" />
              <stop offset="100%" className="[stop-color:hsl(var(--foreground))]" />
            </linearGradient>
          </defs>

          <rect x="0" y="0" width={SVG_SIZE} height={SVG_SIZE} fill="url(#curve-grid)" />

          {/* Diagonal reference line */}
          <line
            x1="0"
            y1={SVG_SIZE}
            x2={SVG_SIZE}
            y2="0"
            stroke="currentColor"
            strokeWidth="1"
            opacity="0.3"
            strokeDasharray="4,4"
          />

          {/* Curve path */}
          <path
            d={createSvgPath(value.points)}
            fill="none"
            stroke="url(#curve-gradient)"
            strokeWidth="3"
            strokeLinecap="round"
          />

          {/* Control points */}
          {value.points.map((point, index) => {
            const isDragged = draggedPointIndex === index;

            // Pre-calculate coordinates outside JSX
            const circleX = point.x * SVG_SIZE;
            const circleY = (1 - point.y) * SVG_SIZE;

            return (
              <circle
                key={`point-${point.x}-${point.y}`}
                cx={circleX}
                cy={circleY}
                r={isDragged ? "5" : "4"}
                fill="white"
                stroke="url(#curve-gradient)"
                strokeWidth="2"
                className="cursor-move pointer-events-none md:pointer-events-auto"
                onMouseDown={(e) => handleMouseDown(index, e)}
                onContextMenu={(e) => handlePointRightClick(index, e)}
              />
            );
          })}
        </svg>
      </div>

      <div className="space-y-1 text-center text-muted-foreground text-xs">
        <p>Click to add, right-click to remove points</p>
        <p>Drag to adjust</p>
        <p>{8 - value.points.length}/6 points remaining</p>
      </div>
    </div>
  );
}
